import HowToRegOutlined from '@mui/icons-material/HowToRegOutlined';
import PersonAddAltOutlined from '@mui/icons-material/PersonAddAltOutlined';
import { ButtonBase } from '@mui/material';
import { CleaningMission } from '@silbo/core';
import { colors, palette } from '@silbo/ui';
import { useTranslation } from 'react-i18next';
import { useAuthenticationContext } from '../../../contexts/AuthenticationContext';
import { useConfigContext } from '../../../contexts/ConfigContext/useConfigContext';
import { useCleaningMissionGestures } from '../hooks/useCleaningMissionGestures';
import { canCancelReservation, canReserve } from '../utils/cleaning-mission-gestures';

type CleaningMissionReserveButtonProps = {
  mission: CleaningMission;
};

const BUTTON_SX = {
  flex: 'none',
  width: 48,
  height: 48,
  borderRadius: '24px',
  '& .MuiSvgIcon-root': { fontSize: 22 },
} as const;

const CleaningMissionReserveButton = ({ mission }: CleaningMissionReserveButtonProps) => {
  const { t } = useTranslation('environmental-cleaning');
  const { user } = useAuthenticationContext();
  const { config } = useConfigContext();
  const { reserve, cancelReservation, isPending } = useCleaningMissionGestures(mission.id);

  const viewer = { userId: user?.id, canCleanBeds: !!config?.cleanBeds };
  const agentId = mission.cleaner?.id ?? mission.assignee?.id;
  const isTakenByAnother = !!agentId && agentId !== user?.id;

  if (canCancelReservation(mission, viewer)) {
    return (
      <ButtonBase
        aria-label={t('actions.cancelReservation')}
        disabled={isPending}
        onClick={() => cancelReservation()}
        sx={{ ...BUTTON_SX, bgcolor: palette.primary.main, color: 'common.white' }}
      >
        <HowToRegOutlined />
      </ButtonBase>
    );
  }
  if (canReserve(mission, viewer)) {
    return (
      <ButtonBase
        aria-label={t('actions.reserve')}
        disabled={isPending}
        onClick={() => reserve()}
        sx={{ ...BUTTON_SX, border: `1.5px solid ${palette.primary.main}`, color: palette.primary.main }}
      >
        <PersonAddAltOutlined />
      </ButtonBase>
    );
  }
  if (isTakenByAnother) {
    const name = (mission.cleaner ? mission.cleanerName : mission.assigneeName) ?? '';
    return (
      <ButtonBase
        disabled
        aria-label={mission.cleaner ? t('missions.row.inProgressBy', { name }) : t('mission.reservedBy', { name })}
        sx={{ ...BUTTON_SX, bgcolor: colors.grey[5], color: `${colors.grey[70]} !important` }}
      >
        <HowToRegOutlined />
      </ButtonBase>
    );
  }
  return null;
};

export default CleaningMissionReserveButton;
